package dev.lingvo.site.pages.app.projects.slug.screenshots

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.styleModifier
import androidx.compose.runtime.getValue
import androidx.compose.runtime.setValue
import dev.lingvo.site.state.authoritySubject
import dev.lingvo.site.api.screenshotDesign
import dev.lingvo.site.api.currentScreenshotApproval
import dev.lingvo.site.api.screenshotPushJobs
import dev.lingvo.site.api.screenshotPlan
import dev.lingvo.site.api.screenshotCoverage
import dev.lingvo.site.ds.LEmptyState
import dev.lingvo.site.ds.LEmptyStateIcon
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LStep
import dev.lingvo.site.ds.LSteps
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tPlural

/** Gives a new project a concrete next action in the place its coverage will appear. */
@Composable
fun FirstRun(canPlan: Boolean, onPlan: () -> Unit, onImport: () -> Unit, canImportStore: Boolean = false, onStoreImport: () -> Unit = {}) {
    LEmptyState(
        title = t("app.projects.slug.screenshots.first_run.no_shots_planned_yet", "No shots planned yet"),
        description = t("app.projects.slug.screenshots.first_run.a_store_page_shows_two_to", "A store page shows two to ten screenshots. Name the five that sell your app, say how to capture each one, and an agent can take them in every language."),
        icon = LEmptyStateIcon.FILES,
        action = if (canPlan) ({ LButton(t("app.projects.slug.screenshots.first_run.plan_five_shots", "Plan five shots"), onPlan) }) else null,
        secondaryAction = if (canPlan) ({ LButton(t("app.projects.slug.screenshots.first_run.import_from_fastlane", "Import from fastlane"), onImport,
            variant = LButtonVariant.SECONDARY) }) else null,
    )
    if (canImportStore) LButton(t("app.projects.slug.screenshots.first_run.start_from_your_current_store_screenshots", "Start from your current store screenshots"), onStoreImport, modifier = Modifier.styleModifier { property("max-width", "100%"); property("white-space", "normal") },
        variant = LButtonVariant.SECONDARY)
}

/** Keeps the checklist truthful when the user returns halfway through the capture workflow. */
@Composable
fun ShotSetupChecklist(completed: Set<Int>) {
    LSteps(steps = listOf(
        LStep(t("app.projects.slug.screenshots.first_run.plan_the_shots", "Plan the shots")),
        LStep(t("app.projects.slug.screenshots.first_run.capture_one_language", "Capture one language")),
        LStep(t("app.projects.slug.screenshots.first_run.pick_a_template_and_brand", "Pick a template and brand")),
        LStep(t("app.projects.slug.screenshots.first_run.capture_every_language", "Capture every language")),
        LStep(t("app.projects.slug.screenshots.first_run.review_and_push", "Review and push")),
    ), current = (0..4).firstOrNull { it !in completed } ?: 5, completed = completed,
        label = t("app.projects.slug.screenshots.first_run.screenshot_setup", "Screenshot setup"))
}

/** Stores shows counts and a next link for both platforms even before a store connection exists. */
@Composable
fun StoreScreenshotsSummary(slug: String) {
    val subject = dev.lingvo.site.state.liveProjectRoute(slug)?.authoritySubject(dev.lingvo.site.api.Session.organizations)
    dev.lingvo.site.ds.LSectionHeading(t("app.projects.slug.screenshots.first_run.screenshots", "Screenshots"), t("app.projects.slug.screenshots.first_run.plan_capture_and_review_the_images", "Plan, capture and review the images for each store."))
    dev.lingvo.shared.StorePlatform.entries.forEach { platform ->
        var retry by androidx.compose.runtime.remember(slug, platform) { androidx.compose.runtime.mutableStateOf(0) }
        val loaded = dev.lingvo.site.api.loadReloadable(subject, platform, reloadToken = retry) {
            val live = subject ?: kotlinx.coroutines.awaitCancellation()
            dev.lingvo.site.state.requestProjectResource(live.action, slug, dev.lingvo.site.api.Session.organizations) {
                val plan = dev.lingvo.site.api.ApiClient.screenshotPlan(slug, platform)
                plan to dev.lingvo.site.api.ApiClient.screenshotCoverage(slug, platform).first
            }
        }
        when (val state = loaded.state) {
            dev.lingvo.site.api.Async.Loading -> dev.lingvo.site.ds.LoadingState(rows = 1)
            is dev.lingvo.site.api.Async.Failure -> dev.lingvo.site.ds.AsyncFailureState(state,
                subject = t("app.projects.slug.screenshots.first_run.the_screenshot_summary", "the screenshot summary"), onRetry = { retry++ })
            is dev.lingvo.site.api.Async.Success -> {
                val (plan, coverage) = state.value
                val owed = coverage?.totals?.let { it.missing + it.stale } ?: 0
                val params = ScreenshotsAreaState(platform)
                if (plan.slots.isEmpty()) dev.lingvo.site.ds.LLinkButton(t("app.projects.slug.screenshots.first_run.no_screenshots_planned_plan_them", "No screenshots planned. Plan them"),
                    screenshotsPath(slug, ScreenshotsTab.PLAN, params), variant = LButtonVariant.SECONDARY)
                else {
                    dev.lingvo.site.ds.LMuted(tf("screenshots.stores.counts", "{store}: {shots} · {languages} · {retakes} to retake",
                        "store" to platform.displayName, "shots" to tPlural("screenshots.stores.shots", plan.slots.size, "{count} shot", "{count} shots"),
                        "languages" to tPlural("screenshots.stores.languages", coverage?.locales?.size ?: 0, "{count} language", "{count} languages"), "retakes" to owed))
                    if (owed == 0 && coverage != null) dev.lingvo.site.ds.LMuted(t("app.projects.slug.screenshots.first_run.every_capture_is_current", "Every capture is current."))
                    dev.lingvo.site.ds.LLinkButton(t("app.projects.slug.screenshots.first_run.open_screenshots", "Open screenshots"), if (owed > 0) screenshotsAgentsPath(slug, params)
                        else screenshotsPath(slug, ScreenshotsTab.PLAN, params), variant = LButtonVariant.SECONDARY)
                }
            }
        }
    }
}

/** Missing designs/approvals are ordinary first-run facts; unavailable services leave a visible partial state. */
suspend fun readShotSetupFacts(slug: String, platform: dev.lingvo.shared.StorePlatform): ShotSetupFacts {
    var failure: String? = null
    suspend fun <T> read(missing: String, block: suspend () -> T): T? = try { block() }
    catch (cancelled: kotlinx.coroutines.CancellationException) { throw cancelled }
    catch (error: dev.lingvo.site.api.ApiException) {
        if (error.status != 404 || error.code != missing) failure = shotRequestFailure(error)
        null
    } catch (error: Throwable) { failure = shotRequestFailure(error); null }
    val design = read("design_not_found") { dev.lingvo.site.api.ApiClient.screenshotDesign(slug, platform) }
    val approval = read("approval_not_found") { dev.lingvo.site.api.ApiClient.currentScreenshotApproval(slug, platform) }
    val pushed = if (approval?.valid == true) read("") { dev.lingvo.site.api.ApiClient.screenshotPushJobs(slug, platform) }.orEmpty()
        .any { it.approvalId == approval.id && it.state == dev.lingvo.shared.ShotPushState.SUCCEEDED } else false
    return ShotSetupFacts(design != null, pushed, failure)
}
